import { CHART_MARKDOWN_ROWS, CHART_TYPE_LABELS, CHART_TYPES, } from './chilldoc-chart.js';
import { slideBreakMarkdown } from './slides.js';
import { calloutKindOf } from './chilldoc-callout.js';
import { captionKindOf, captionLabel, crossRefText, refIndexOfJson, VI_REF_LABELS, } from './chilldoc-refs.js';
import { SUGGEST_INSERT_MARK } from './chilldoc-suggest.js';
/**
 * Cross-references and caption numbers of the document being written (APM-CD2-d): set for the
 * duration of one synchronous `chilldocToMarkdown` call so the inline writer reads them.
 */
let refs = null;
/**
 * `index`: numbering of the **whole** document when `doc` is only part of it (a slide
 * region) — without it, references are resolved against `doc` itself.
 */
export function chilldocToMarkdown(doc, index) {
    const previous = refs;
    refs = index ?? refIndexOfJson(doc);
    try {
        const blocks = (doc.content ?? [])
            .map((node) => block(node, ''))
            .filter((text) => text !== null);
        return `${blocks
            .join('\n\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim()}\n`;
    }
    finally {
        refs = previous;
    }
}
/** `"Hình 2: "` before a caption paragraph (APM-CD2-d); `""` for other paragraphs. */
function captionPrefix(node) {
    const kind = captionKindOf(node.attrs?.['caption']);
    if (!kind)
        return '';
    const id = node.attrs?.['anchorId'];
    const target = typeof id === 'string' ? refs?.byId.get(id) : undefined;
    return target ? `${captionLabel(kind, target.number, VI_REF_LABELS)}: ` : '';
}
function block(node, indent) {
    switch (node.type) {
        case 'paragraph':
            return indent + captionPrefix(node) + escapeLineStart(inline(node.content));
        case 'heading': {
            const level = Math.min(6, Math.max(1, Number(node.attrs?.['level'] ?? 1)));
            return `${indent}${'#'.repeat(level)} ${inline(node.content)}`;
        }
        // Checklist `taskList` (APM-CD2-e): GFM `- [ ]` / `- [x]` — dòng gắn task giữ chữ, id ở JSON.
        case 'bulletList':
        case 'orderedList':
        case 'taskList':
            return list(node, indent);
        case 'blockquote':
            return (node.content ?? [])
                .map((child) => block(child, ''))
                .filter((text) => text !== null)
                .join('\n\n')
                .split('\n')
                .map((line) => `${indent}> ${line}`.trimEnd())
                .join('\n');
        case 'callout': {
            // Callout box (APM-CD2-a): HTML block of GFM — markdown inside stays readable.
            const inner = (node.content ?? [])
                .map((child) => block(child, indent))
                .filter((text) => text !== null)
                .join('\n\n');
            const kind = calloutKindOf(node.attrs?.['kind']);
            return `${indent}<aside data-callout="${kind}">\n\n${inner}\n\n${indent}</aside>`;
        }
        case 'codeBlock': {
            const language = typeof node.attrs?.['language'] === 'string' ? node.attrs['language'] : '';
            const code = (node.content ?? []).map((child) => child.text ?? '').join('');
            return `${indent}\`\`\`${language}\n${code}\n\`\`\``;
        }
        case 'horizontalRule':
            return `${indent}---`;
        case 'pageBreak':
            // Ngắt trang của Word (APM-CD-b): markdown không có — chú thích để người đọc biết.
            return `${indent}<!-- ngắt trang -->`;
        case 'table':
            return table(node);
        case 'image':
        case 'fileRef':
            return indent + inline([node]);
        case 'drawing':
            return drawing(node, indent);
        case 'chart':
            return chart(node, indent);
        case 'slideBreak':
            // Thanh ngăn trình chiếu (APM-SLD-a): chữ trong slide phải tìm được.
            return slideBreakMarkdown(node.attrs)
                .map((line) => indent + line)
                .join('\n');
        case 'blockMath':
            // Công thức (APM-CD-j): LaTeX như markdown/Pandoc — LLM đọc được.
            return `${indent}$$\n${String(node.attrs?.['latex'] ?? '')}\n$$`;
        default:
            // Node lạ của bản sau: vẫn giữ chữ bên trong, không đánh rơi nội dung.
            return node.content ? indent + inline(node.content) : null;
    }
}
function list(node, indent) {
    const ordered = node.type === 'orderedList';
    const start = Number(node.attrs?.['start'] ?? 1);
    return (node.content ?? [])
        .map((item, index) => {
        const marker = ordered
            ? `${start + index}. `
            : node.type === 'taskList'
                ? `- [${item.attrs?.['checked'] === true ? 'x' : ' '}] `
                : '- ';
        // Dòng đầu là tiêu đề (đánh số một tiêu đề, như Word) ⇒ `1. ## Tiêu đề`.
        const children = (item.content ?? []).map((child, childIndex) => childIndex === 0 && child.type === 'paragraph'
            ? inline(child.content)
            : childIndex === 0 && child.type === 'heading'
                ? (block(child, '') ?? '')
                : (block(child, `${indent}  `) ?? ''));
        const [first = '', ...rest] = children;
        return [`${indent}${marker}${first}`, ...rest].join('\n');
    })
        .join('\n');
}
/** GFM table. Ô có công thức (APM-CD-f) ghi công thức — giá trị do app tính lúc mở. */
function table(node) {
    const rows = (node.content ?? []).map((row) => (row.content ?? []).map((cell) => {
        const formula = cell.attrs?.['formula'];
        const text = typeof formula === 'string' && formula
            ? `\`${formula}\``
            : (cell.content ?? []).map((child) => inline(child.content)).join(' ');
        return text.replace(/\|/g, '\\|').replace(/\n/g, ' ');
    }));
    if (!rows.length)
        return '';
    const width = Math.max(...rows.map((row) => row.length));
    const pad = (row) => [...row, ...Array(width - row.length).fill('')];
    const line = (row) => `| ${pad(row).join(' | ')} |`;
    const [head, ...body] = rows;
    return [
        line(head),
        `| ${Array(width).fill('---').join(' | ')} |`,
        ...body.map(line),
    ].join('\n');
}
function inline(nodes) {
    return (nodes ?? [])
        .map((node) => {
        // Suggestions (APM-CD2-h): the head markdown (search, AI) is the unreviewed text, i.e. as
        // if every suggestion were rejected — proposed insertions are not part of it yet (QA 2026-10-13).
        if ((node.marks ?? []).some((mark) => mark.type === SUGGEST_INSERT_MARK))
            return '';
        if (node.type === 'hardBreak')
            return '  \n';
        if (node.type === 'image') {
            const alt = String(node.attrs?.['alt'] ?? '');
            return `![${alt}](${String(node.attrs?.['src'] ?? '')})`;
        }
        if (node.type === 'inlineMath')
            return `$${String(node.attrs?.['latex'] ?? '')}$`;
        // Cross-reference (APM-CD2-d): the text the reader sees; bookmarks are invisible.
        if (node.type === 'crossRef') {
            return refs
                ? crossRefText(refs, node.attrs?.['targetId'], node.attrs?.['show'], VI_REF_LABELS).text
                : '';
        }
        if (node.type === 'bookmark')
            return '';
        // Mentions (APM-CD2-f): the label captured when inserted — `@Name`, `#KEY Title`.
        if (node.type === 'mention')
            return `@${String(node.attrs?.['label'] ?? '')}`;
        if (node.type === 'entityRef')
            return `#${String(node.attrs?.['label'] ?? '')}`;
        if (node.type === 'fileRef') {
            const id = String(node.attrs?.['fileId'] ?? '');
            return `[${String(node.attrs?.['name'] ?? id)}](apm-file:${id})`;
        }
        return marked(node.text ?? '', node.marks ?? []);
    })
        .join('');
}
function marked(text, marks) {
    if (!text)
        return '';
    let out = text;
    const has = (type) => marks.some((mark) => mark.type === type);
    if (has('code'))
        return `\`${out}\``;
    if (has('bold'))
        out = `**${out}**`;
    if (has('italic'))
        out = `*${out}*`;
    if (has('strike'))
        out = `~~${out}~~`;
    const link = marks.find((mark) => mark.type === 'link');
    const href = link?.attrs?.['href'];
    if (typeof href === 'string')
        out = `[${out}](${href})`;
    return out;
}
/**
 * Đoạn văn **thường** mà chữ bắt đầu bằng ký hiệu markdown (`- `, `# `, `1. `,
 * `> `) phải được escape — không thì người/LLM đọc markdown thấy một danh sách
 * hay tiêu đề không có trong tài liệu.
 */
function escapeLineStart(text) {
    return text
        .replace(/^(\s*)([-*+>#])(?=\s|$)/, '$1\\$2')
        .replace(/^(\s*\d+)([.)])(?=\s|$)/, '$1\\$2');
}
const oneLine = (text) => text.replace(/\s+/g, ' ').trim();
/**
 * Khu vực vẽ (APM-DRW-b, plan-drawing §3.3) ⇒ vài dòng chữ: LLM và ô tìm kiếm
 * phải đọc được chữ trong hình. Mũi tên bám hai hình ⇒ `A → B`; chữ không nằm
 * trong hình hay mũi tên nào ⇒ "Ô chữ khác".
 */
function drawing(node, indent) {
    const width = Number(node.attrs?.['width'] ?? 0);
    const height = Number(node.attrs?.['height'] ?? 0);
    const raw = node.attrs?.['elements'];
    const elements = (Array.isArray(raw) ? raw : []).filter((element) => element && !element.isDeleted);
    const labelOf = new Map();
    const loose = [];
    for (const element of elements) {
        if (element.type !== 'text' || !element.text?.trim())
            continue;
        if (element.containerId)
            labelOf.set(element.containerId, oneLine(element.text));
        else
            loose.push(oneLine(element.text));
    }
    const edges = [];
    const linked = new Set();
    for (const element of elements) {
        if (element.type !== 'arrow')
            continue;
        const from = element.startBinding?.elementId;
        const to = element.endBinding?.elementId;
        if (!from || !to)
            continue;
        linked.add(from).add(to);
        const label = element.id ? labelOf.get(element.id) : undefined;
        edges.push(`${labelOf.get(from) ?? '?'} → ${labelOf.get(to) ?? '?'}${label ? ` (${label})` : ''}`);
    }
    const shapes = elements
        .filter((element) => element.type !== 'arrow' && element.id && !linked.has(element.id))
        .map((element) => labelOf.get(element.id))
        .filter((label) => Boolean(label));
    const spec = node.attrs?.['spec'];
    const frame = elements.find((element) => element.type === 'frame')?.name;
    const title = typeof spec?.title === 'string' && spec.title ? spec.title : (frame ?? null);
    const body = [...edges, ...shapes].join('; ');
    const lines = [`${indent}<!-- hình vẽ ${width}×${height} -->`];
    if (body || title)
        lines.push(`${indent}**Hình vẽ${title ? ` — ${oneLine(title)}` : ''}:** ${body}`.trimEnd());
    if (loose.length)
        lines.push(`${indent}Ô chữ khác: ${loose.join('; ')}`);
    // Hình chuyển từ Mermaid (APM-MMD-a): mã gốc là bản đọc đầy đủ nhất, kể cả loại sơ đồ.
    const mermaid = node.attrs?.['mermaid'];
    if (typeof mermaid === 'string' && mermaid.trim()) {
        lines.push(`${indent}\`\`\`mermaid`, ...mermaid
            .trimEnd()
            .split('\n')
            .map((line) => indent + line), `${indent}\`\`\``);
    }
    return lines.join('\n');
}
const textOf = (value) => typeof value === 'string' && value.trim() ? oneLine(value) : null;
const cellText = (value) => (typeof value === 'number' ? String(value) : (textOf(value) ?? '')).replace(/\|/g, '\\|');
/**
 * Biểu đồ (APM-CH-b, plan-doc-charts §3.4) ⇒ tiêu đề + loại + nhãn trục + câu
 * insight + bảng số từ `snapshot` (≤ 30 hàng). Đọc thô từng trường như
 * `drawing` — spec của bản sau hỏng schema vẫn ra được chữ.
 */
function chart(node, indent) {
    const spec = (node.attrs?.['spec'] ?? {});
    const type = CHART_TYPES.includes(spec['type']) ? spec['type'] : null;
    const typeLabel = type ? CHART_TYPE_LABELS[type] : null;
    const title = textOf(spec['title']);
    const xLabel = textOf(spec['xLabel']);
    const yLabel = textOf(spec['yLabel']);
    // `xLabel` là trục nhóm, `yLabel` trục giá trị — `horizontal_bar` vẽ trục nhóm ở chiều dọc.
    const [across, down] = type === 'horizontal_bar' ? [yLabel, xLabel] : [xLabel, yLabel];
    const facts = [
        typeLabel,
        across ? `trục ngang: ${across}` : null,
        down ? `trục dọc: ${down}` : null,
    ].filter((fact) => fact !== null);
    const lines = [`${indent}<!-- biểu đồ${typeLabel ? ` ${typeLabel}` : ''} -->`];
    lines.push(`${indent}**Biểu đồ${title ? ` — ${title}` : ''}**${facts.length ? ` (${facts.join('; ')})` : ''}`);
    const insight = textOf(spec['insight']);
    if (insight)
        lines.push(`${indent}_${insight}_`);
    const snapshot = node.attrs?.['snapshot'];
    const columns = Array.isArray(snapshot?.columns) ? snapshot.columns : [];
    const rows = (Array.isArray(snapshot?.rows) ? snapshot.rows : []).filter((row) => Array.isArray(row));
    if (columns.length) {
        const line = (cells) => `${indent}| ${columns.map((_, index) => cellText(cells[index])).join(' | ')} |`;
        lines.push(line(columns), `${indent}| ${columns.map(() => '---').join(' | ')} |`);
        for (const row of rows.slice(0, CHART_MARKDOWN_ROWS))
            lines.push(line(row));
        if (rows.length > CHART_MARKDOWN_ROWS) {
            lines.push(`${indent}… và ${rows.length - CHART_MARKDOWN_ROWS} hàng nữa`);
        }
        if (snapshot?.truncated === true)
            lines.push(`${indent}_(bản chụp số liệu đã cắt bớt)_`);
    }
    return lines.join('\n');
}
//# sourceMappingURL=chilldoc-markdown.js.map